import {
  useContestMakeupReviewGet,
  useContestMakeupWaive,
} from "@sportsmet/contest-sdk/private";
import { Button, confirmDestructive, InlineBanner } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { countOf } from "../survivorGame/survivorGameCopy";
import { ReviewReasonField } from "./ReviewReasonField";
type Preview = NonNullable<
  NonNullable<ReturnType<typeof useContestMakeupReviewGet>["data"]>["data"]
>;
type Attempt = {
  poolId: string;
  contestId: string;
  reviewToken: string;
  requestId: string;
  reason: string;
};
/** Mounted only for the commissioner. Server independently requires owner.
 * API failures go to the shared error modal. The inline banner carries only the
 * stale-review CONFLICT and neutral or success notices.
 */
export function ContestMakeupReview({
  poolId,
  contestId,
}: {
  poolId: string;
  contestId: string;
}): ReactNode {
  const get = useContestMakeupReviewGet(
    { poolId, contestId },
    { enabled: false },
  );
  const waive = useContestMakeupWaive();
  const client = useQueryClient();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [loading, setLoading] = useState(false);
  async function load() {
    setLoading(true);
    setPreview(null);
    setNotice(null);
    setReason("");
    try {
      const result = await get.refetch();
      if (showApiError(result.data)) return;
      if (result.isError) {
        showOfflineError();
        return;
      }
      setPreview(result.data?.data ?? null);
      if (!result.data?.data || result.data.data.outstandingPickCount === 0)
        setNotice("No outstanding makeup picks.");
    } catch {
      showOfflineError();
    } finally {
      setLoading(false);
    }
  }
  async function submit() {
    if (waive.isPending) return;
    let body = attempt;
    if (!body) {
      if (
        !preview?.canWaive ||
        !reason.trim() ||
        preview.outstandingPickCount === 0
      )
        return;
      const accepted = await confirmDestructive({
        title: "Waive outstanding makeup picks?",
        body: `This forgives ${countOf(preview.outstandingPickCount, "makeup pick", "makeup picks")} for ${countOf(preview.affectedEntryCount, "entry", "entries")}. It removes those obligations and records your reason. It does not award points or confirm the contest.`,
        confirmLabel: "Waive makeup picks",
        cancelLabel: "Keep obligations",
      });
      if (!accepted) return;
      body = {
        poolId,
        contestId,
        reviewToken: preview.reviewToken,
        requestId: crypto.randomUUID(),
        reason: reason.trim(),
      };
      setAttempt(body);
    }
    try {
      const result = await waive.mutateAsync(body);
      if (extractErrorCode(result) === "CONFLICT") {
        setPreview(null);
        setAttempt(null);
        setNotice(
          "The contest or makeup obligations changed. Review them again before deciding.",
        );
        return;
      }
      const code = extractErrorCode(result);
      if (code !== null) {
        // A refusal will not change on a resend, so it drops the attempt. Any
        // other failure keeps it: the button then resends the same requestId,
        // which is safe and reports the real outcome.
        if (
          [
            "FORBIDDEN_ROLE",
            "FORBIDDEN_POOL",
            "VALIDATION_ERROR",
            "UNAUTHENTICATED",
          ].includes(code)
        ) {
          setAttempt(null);
        }
        showApiError(result);
        return;
      }
      if (!result.data) throw new Error("Unknown waiver outcome");
      setAttempt(null);
      setPreview(null);
      setNotice(
        `${countOf(result.data.waivedPickCount, "makeup pick", "makeup picks")} waived. Your reason was recorded. Review the contest result separately before confirming.`,
      );
      await client.invalidateQueries({
        predicate: (query) =>
          String(query.queryKey[0]).startsWith("post:contest:"),
      });
    } catch {
      showOfflineError();
    }
  }
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-line p-4"
      aria-label="Makeup pick review"
    >
      <h3 className="text-h3 text-ink">Outstanding makeup picks</h3>
      <p className="text-sm text-ink-muted">
        Only the commissioner can waive obligations that cannot be completed
        before the contest ends.
      </p>
      <Button
        variant="secondary"
        disabled={loading || waive.isPending || attempt !== null}
        loading={loading}
        onClick={() => {
          void load();
        }}
      >
        Review makeup obligations
      </Button>
      {notice && <InlineBanner variant="neutral" message={notice} />}
      {preview && preview.outstandingPickCount > 0 && (
        <>
          <p>
            {countOf(
              preview.outstandingPickCount,
              "outstanding pick",
              "outstanding picks",
            )}{" "}
            across {countOf(preview.affectedEntryCount, "entry", "entries")}
            {preview.firstSourceRoundNumber === null
              ? "."
              : `, beginning in round ${preview.firstSourceRoundNumber}.`}
          </p>
          {!preview.canWaive && (
            <InlineBanner
              variant="warning"
              message="Waiving is unavailable while play or grading remains open. Complete the active rounds and review the pending contest outcome first."
            />
          )}
          <ReviewReasonField
            label="Reason for waiver (required)"
            value={reason}
            disabled={waive.isPending || attempt !== null}
            onChange={setReason}
          />
          <Button
            variant="secondary"
            disabled={
              waive.isPending ||
              (!attempt && (!preview.canWaive || !reason.trim()))
            }
            loading={waive.isPending}
            onClick={() => {
              void submit();
            }}
          >
            {attempt
              ? "Retry same waiver request"
              : "Waive outstanding makeup picks"}
          </Button>
        </>
      )}
    </section>
  );
}
